import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import {
  resolveFocusMenuSide,
  FOCUS_MENU_DEFAULT_SIZE,
} from 'dashboard/composables/useFocusMode';

/**
 * SiteUp — por que estes menus da sidebar são teleportados
 *
 * A sidebar tem `position: relative` + `z-index: 40` (`Sidebar.vue`), e isso
 * cria um CONTEXTO DE EMPILHAMENTO: qualquer coisa renderizada dentro dela
 * fica presa nesse contexto, por mais alto que seja o próprio z-index. Um
 * dropdown com `z-50` lá dentro continua atrás da lista de conversas — some
 * tudo que passa da largura da sidebar (com ela recolhida, sobra uma fatia
 * de ~56px e o menu fica ilegível).
 *
 * A saída é a mesma que o `SidebarCollapsedPopover.vue` já usava: teleportar
 * pro <body> e posicionar à mão a partir do retângulo do gatilho.
 *
 * O cluster é arrastável, então o lado preferido pode não caber. A decisão de
 * virar é do `resolveFocusMenuSide`, que já considera os QUATRO lados. A versão
 * anterior deste arquivo pedia a decisão e depois traduzia tudo para cima/baixo,
 * jogando fora justamente a resposta certa: com o cluster no topo à direita o
 * menu abria para baixo, em cima dos cinco botões (medido: 87% de cobertura).
 *
 * Duas escolhas seguram o invariante "o menu nunca cobre o cluster":
 * 1) o retângulo de referência do eixo principal é o CLUSTER INTEIRO, não o
 *    botãozinho — sair de fora do botão ainda aterrissa sobre os irmãos dele;
 * 2) o eixo principal ancora pelo lado oposto (`left` fixa a direita do menu em
 *    `cluster.left - gap`), então nenhum deslocamento no eixo cruzado cria
 *    interseção. Cobrir o cluster passa a ser impossível, não improvável.
 *
 * Medida: o menu só existe depois de aberto (teleport + v-if). Fluxo:
 * 1) `measure()` com a última medida conhecida (ou fallback) posiciona na hora;
 * 2) após o mount, `remeasure()` lê offset/scroll e reposiciona.
 *
 * @param {'top'|'bottom'} placement lado preferido. 'top' tenta abrir pra cima,
 *   'bottom' pra baixo. Se não couber, o automático decide (inclusive lateral).
 * @param {number} gap distância em px entre o cluster e o menu.
 */
export function useSidebarDropdownAnchor({ placement = 'top', gap = 8 } = {}) {
  const isRTL = useMapGetter('accounts/isRTL');

  const triggerRef = ref(null);
  const menuRef = ref(null);
  const anchorStyle = ref({});
  // Teto de altura pro menu nunca passar da tela numa janela baixa — sem isto
  // o último item (ex.: "Encerrar sessão") ficaria inalcançável.
  const menuMaxHeight = ref(undefined);
  // Lado efetivo, para quem quiser animar a entrada na direção certa.
  const menuSide = ref('up');
  // Última medida real do painel (persiste entre aberturas do mesmo menu).
  const lastMenuSize = ref(null);

  const EDGE = 8;

  const CLUSTER = '[data-testid="focus-mode-cluster"]';

  const readMenuSize = () => {
    const menu = menuRef.value;
    if (!menu) return lastMenuSize.value || {};
    // offset/scroll e nunca getBoundingClientRect: o painel entra sob
    // `Transition scale-95`, e o rect no meio da animação vem 5% menor.
    const medir = el => ({
      width: Math.max(el.offsetWidth || 0, el.scrollWidth || 0),
      height: Math.max(el.scrollHeight || 0, el.offsetHeight || 0),
    });
    // Medir SEMPRE os dois e ficar com o maior. O teto de altura que este
    // composable aplica vive no painel interno (é ele que tem overflow-y-auto),
    // então o wrapper devolve a altura JÁ CORTADA e a leitura entra em ciclo:
    // lê 478 → aplica teto de 120 → lê 120 → acha que cabe → tira o teto →
    // lê 478, para sempre, com o menu piscando. O scrollHeight do painel é
    // imune ao teto, e é ele quem tem que mandar.
    const proprio = medir(menu);
    const filho = menu.firstElementChild
      ? medir(menu.firstElementChild)
      : { width: 0, height: 0 };
    const size = {
      width: Math.max(proprio.width, filho.width),
      height: Math.max(proprio.height, filho.height),
    };
    if (size.width > 0 && size.height > 0) {
      lastMenuSize.value = size;
    }
    return lastMenuSize.value || size;
  };

  // `size` é a MESMA medida que escolheu o lado. Reler o DOM aqui deixava a
  // decisão e o clamp discordando: com o cluster no canto direito o lado saía
  // por 320px de largura e a trava usava os 224 do fallback, vazando 88px.
  const applyAnchor = (side, base, trig, size) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // O mesmo fallback que `measure` usa para escolher o lado. Antes eram dois
    // diferentes (0 aqui, 320 lá), então a primeira abertura decidia com um
    // número e travava a altura com outro.
    const conhecida = size.height > 0;
    const alturaMenu = size.height || FOCUS_MENU_DEFAULT_SIZE.height;
    const larguraMenu = size.width || FOCUS_MENU_DEFAULT_SIZE.width;
    const horizontal = side === 'left' || side === 'right';

    // Aberto na lateral o menu tem a tela inteira em altura; aberto em
    // cima/baixo ele só tem a faixa entre o cluster e a borda.
    const faixa = {
      up: base.top - gap - EDGE,
      down: vh - base.bottom - gap - EDGE,
    };
    const disponivel = horizontal
      ? vh - EDGE * 2
      : Math.max(0, faixa[side] ?? 0);
    // Teto só quando ele de fato RESTRINGE, e só com a altura conhecida. Um
    // `max-height` maior que o conteúdo não protege nada e ainda apaga o limite
    // que o próprio componente declara: o sino tem `max-h-[480px]`, e o teto de
    // 884px que saía da primeira abertura (altura ainda desconhecida) inflava o
    // painel de 480px para 884px e jogava o cabeçalho 375px acima da tela.
    // Sem teto nosso, o CSS do componente manda e o observador corrige depois.
    const teto =
      conhecida && disponivel < alturaMenu
        ? Math.max(1, Math.min(Math.round(disponivel), vh - EDGE * 2))
        : null;
    menuMaxHeight.value = teto === null ? undefined : `${teto}px`;

    // Eixo principal: sempre a partir do retângulo do CLUSTER, ancorando pelo
    // lado oposto. É esta linha que torna a sobreposição impossível.
    const estilo = {};
    if (side === 'up') estilo.bottom = `${Math.round(vh - base.top + gap)}px`;
    if (side === 'down') estilo.top = `${Math.round(base.bottom + gap)}px`;
    if (side === 'left') estilo.right = `${Math.round(vw - base.left + gap)}px`;
    if (side === 'right') estilo.left = `${Math.round(base.right + gap)}px`;

    const prender = (valor, limite) =>
      Math.max(EDGE, Math.min(valor, Math.max(EDGE, limite)));

    // Eixo cruzado: alinhado pelo gatilho (o item que a pessoa clicou), preso
    // dentro da tela. O clamp vale mesmo sem medida conhecida — desligá-lo era
    // o que deixava o menu vazar quando a primeira abertura media zero.
    if (horizontal) {
      const alturaEfetiva =
        teto === null ? alturaMenu : Math.min(alturaMenu, teto);
      estilo.top = `${Math.round(
        prender(trig.top, vh - alturaEfetiva - EDGE)
      )}px`;
    } else {
      const ladoCruzado = isRTL.value ? 'right' : 'left';
      const inicio = isRTL.value ? vw - trig.right : trig.left;
      estilo[ladoCruzado] = `${Math.round(
        prender(inicio, vw - larguraMenu - EDGE)
      )}px`;
    }

    anchorStyle.value = estilo;
    menuSide.value = side;
  };

  const measure = menuSize => {
    const el = triggerRef.value;
    if (!el) return;

    const trig = el.getBoundingClientRect();
    // `closest` com `?.`: os gatilhos falsos dos testes só têm rect.
    const base = el.closest?.(CLUSTER)?.getBoundingClientRect() || trig;
    // Uma medida só para decidir e para prender.
    const size = menuSize || readMenuSize();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const preferred = placement === 'bottom' ? 'down' : 'up';

    let side = resolveFocusMenuSide(
      preferred,
      base,
      { width: vw, height: vh },
      size
    );

    const larguraMenu = size.width || FOCUS_MENU_DEFAULT_SIZE.width;
    const alturaMenu = size.height || FOCUS_MENU_DEFAULT_SIZE.height;
    const espaco = {
      up: base.top - gap - EDGE,
      down: vh - base.bottom - gap - EDGE,
      left: base.left - gap - EDGE,
      right: vw - base.right - gap - EDGE,
    };
    const eixoPrincipal = lado =>
      lado === 'up' || lado === 'down' ? alturaMenu : larguraMenu;
    // Na lateral o eixo principal é a LARGURA, e o menu ainda precisa caber em
    // altura na tela. Em cima/baixo só a faixa até a borda conta.
    const cabeInteiro = lado =>
      espaco[lado] >= eixoPrincipal(lado) &&
      (lado === 'up' || lado === 'down' || vh - EDGE * 2 >= alturaMenu);

    if (!cabeInteiro(side)) {
      // O resolver devolve o lado preferido assim que ele é "usável com
      // rolagem", antes de olhar a lateral. Com o cluster no meio da tela isso
      // espremia o menu do perfil numa faixa de 329px para 344px de conteúdo,
      // quando ao lado ele cabia inteiro. Caber ganha da preferência.
      const inteiro = ['left', 'right', 'up', 'down']
        .filter(cabeInteiro)
        .sort((a, b) => espaco[b] - espaco[a])[0];
      if (inteiro) side = inteiro;
      // Nenhum lado inteiro: sobra rolar. A lateral só se sustenta se a largura
      // couber — o painel não tem max-width, meia largura vazaria da tela.
      else if (espaco[side] < eixoPrincipal(side)) {
        side = espaco.up >= espaco.down ? 'up' : 'down';
      }
    }

    applyAnchor(side, base, trig, size);
  };

  let observador = null;
  const pararDeObservar = () => {
    observador?.disconnect();
    observador = null;
  };

  // Medir uma vez, num nextTick, chega tarde demais: o menu entra por teleport
  // + Transition e ainda não tem tamanho, então a decisão sai do palpite de
  // 320px e nada mais remede. Medido no staging em 1440x600: o menu abria 37px
  // acima do topo da tela e ficava lá — disparar um `resize` à mão consertava
  // sozinho, o que prova que só faltava uma segunda medição.
  const observarMenu = () => {
    pararDeObservar();
    const menu = menuRef.value;
    if (!menu || typeof ResizeObserver === 'undefined') return;

    let ultima = 0;
    observador = new ResizeObserver(() => {
      const size = readMenuSize();
      // Converge porque `readMenuSize` lê a altura natural do painel, que não
      // encolhe quando aplicamos o teto.
      if (!size.height || Math.abs(size.height - ultima) < 2) return;
      ultima = size.height;
      measure(size);
    });
    observador.observe(menu);
  };

  // Depois do menu montar no body: mede de verdade e reposiciona.
  const remeasure = async () => {
    await nextTick();
    measure(readMenuSize());
    observarMenu();
  };

  // Redimensionar a janela move o gatilho; sem isso o menu aberto ficaria
  // ancorado na posição antiga.
  onMounted(() => window.addEventListener('resize', remeasure));
  onBeforeUnmount(() => {
    window.removeEventListener('resize', remeasure);
    pararDeObservar();
  });

  return {
    triggerRef,
    menuRef,
    anchorStyle,
    menuMaxHeight,
    menuSide,
    measure,
    remeasure,
  };
}
